import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Lock, Eye, EyeSlash, CheckCircle, Warning } from 'phosphor-react';

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Get URL parameters
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  const type = searchParams.get('type');

  useEffect(() => {
    const handlePasswordReset = async () => {
      console.log('ResetPassword: Starting password reset flow');
      console.log('URL:', window.location.href);
      console.log('Access token:', accessToken);
      console.log('Refresh token:', refreshToken);
      console.log('Type:', type);

      // Check for error in URL hash
      const hash = location.hash;
      if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const error = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        
        console.log('Hash error:', error, errorCode);
        
        if (error === 'access_denied' && errorCode === 'otp_expired') {
          setError('This password reset link has expired. Please request a new one.');
          setInitializing(false);
          return;
        }
      }

      // If we have access and refresh tokens, set up the session
      if (accessToken && refreshToken) {
        console.log('Setting up session with tokens');
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Error setting session:', error);
            setError('Invalid or expired reset link. Please request a new one.');
            setInitializing(false);
            return;
          }

          console.log('Session set up successfully');
          setInitializing(false);
        } catch (err) {
          console.error('Error setting session:', err);
          setError('Invalid or expired reset link. Please request a new one.');
          setInitializing(false);
        }
      } else {
        console.log('No tokens found, checking if user is already authenticated');
        
        // Check if user is already authenticated (might happen if they're already logged in)
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          console.log('User is already authenticated:', session.user.email);
          setInitializing(false);
        } else {
          console.log('No session found, redirecting to login');
          navigate('/login');
        }
      }
    };

    handlePasswordReset();
  }, [accessToken, refreshToken, type, navigate, location.hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('Updating password...');
      
      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      console.log('Password updated successfully');
      setSuccess(true);
      
      // Sign out the user after password reset
      await supabase.auth.signOut();
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Failed to reset password. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewLink = () => {
    navigate('/forgot-password');
  };

  if (initializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center py-16 px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ambre-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Setting up password reset...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center py-16 px-8">
        <div className="max-w-md w-full">
          {success ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <CheckCircle size={64} className="text-green-500 mx-auto mb-4" weight="fill" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Reset Successful!</h1>
              <p className="text-gray-600 mb-6">
                Your password has been successfully updated. You will be redirected to the login page shortly.
              </p>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ambre-600 mx-auto"></div>
            </div>
          ) : error ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Warning size={32} className="text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Link Error</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="space-y-4">
                <button
                  onClick={handleRequestNewLink}
                  className="w-full bg-ambre-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-ambre-700 transition-colors"
                >
                  Request New Reset Link
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-ambre-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock size={32} className="text-ambre-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Your Password</h1>
                <p className="text-gray-600">Enter your new password below</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                      placeholder="Enter your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                      placeholder="Confirm your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-ambre-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-ambre-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Resetting Password...
                    </div>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/login')}
                  className="text-ambre-600 hover:text-ambre-700 font-medium"
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default ResetPassword; 